import { PartFinderReportImageInfo } from '@/api/generatedApi';
import { useGetPartFinderReportImage } from '@/hooks/api/part-finder-reports/useGetPartFinderReportImage';
import { ChevronLeft, ChevronRight, Close } from '@mui/icons-material';
import { Box, Dialog, IconButton, Typography } from '@mui/material';
import { useEffect } from 'react';

interface ReportImageLightboxProperties {
  reportId: string;
  version?: string;
  images: PartFinderReportImageInfo[];
  activePosition: number;
  caption: string;
  onNavigate: (position: number) => void;
  onClose: () => void;
}

export const ReportImageLightbox = ({
  reportId,
  version,
  images,
  activePosition,
  caption,
  onNavigate,
  onClose,
}: ReportImageLightboxProperties) => {
  const { image: imageUrl } = useGetPartFinderReportImage({ reportId, index: images[activePosition].index, version });
  const hasMultiple = images.length > 1;

  const goTo = (offset: number) => onNavigate((activePosition + offset + images.length) % images.length);

  useEffect(() => {
    const handleKey = (event: KeyboardEvent) => {
      if (event.key === 'ArrowLeft') {
        goTo(-1);
      } else if (event.key === 'ArrowRight') {
        goTo(1);
      }
    };
    globalThis.addEventListener('keydown', handleKey);
    return () => globalThis.removeEventListener('keydown', handleKey);
  });

  return (
    <Dialog open onClose={onClose} maxWidth='lg' fullWidth>
      <Box
        sx={{ position: 'relative', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px', p: 2 }}
      >
        <IconButton aria-label='Close' onClick={onClose} sx={{ position: 'absolute', top: 8, right: 8 }}>
          <Close />
        </IconButton>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', width: '100%' }}>
          {hasMultiple && (
            <IconButton aria-label='Previous image' onClick={() => goTo(-1)}>
              <ChevronLeft />
            </IconButton>
          )}
          <Box sx={{ flex: 1, display: 'flex', justifyContent: 'center', minHeight: 0 }}>
            {imageUrl && (
              <img src={imageUrl} alt={caption} style={{ maxWidth: '100%', maxHeight: '70vh', objectFit: 'contain' }} />
            )}
          </Box>
          {hasMultiple && (
            <IconButton aria-label='Next image' onClick={() => goTo(1)}>
              <ChevronRight />
            </IconButton>
          )}
        </Box>
        <Typography variant='caption' sx={{ color: 'text.secondary' }}>
          {hasMultiple ? `${caption} · ${activePosition + 1} / ${images.length}` : caption}
        </Typography>
      </Box>
    </Dialog>
  );
};
